import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuthInfo, useLogoutFunction } from '@propelauth/react';
import { useAuthFrontendApis } from '@propelauth/frontend-apis-react';
import { LoginState } from '@propelauth/frontend-apis';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../app/store/utils/redux.hooks';
import { getUserId } from '../../../entities/user/domain/user.types';
import { userSelector, userThunksSelector } from '../../../entities/user/store/user.selectors';
import { getUser } from '../../../entities/user/store/user.slice';
import { performFullLogout } from '../../../features/authentication/utils/auth-utils';
import { UserPropertiesForm } from '../../../components/auth/user-properties-form';

export function SuccessAuth() {
  const { isLoggedIn, loading: isAuthLoading, accessToken } = useAuthInfo();
  const { fetchLoginState } = useAuthFrontendApis();
  const logout = useLogoutFunction();
  const user = useAppSelector(userSelector);
  const userId = getUserId(user);
  const { isLoading: isUserLoading } = useAppSelector(userThunksSelector).getUser;
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);
  const [timeoutOccurred, setTimeoutOccurred] = useState(false);
  const [attemptedFetch, setAttemptedFetch] = useState(false);
  const [sessionEstablished, setSessionEstablished] = useState(false);
  const [showUserPropertiesForm, setShowUserPropertiesForm] = useState(false);
  const [hasCheckedLoginState, setHasCheckedLoginState] = useState(false);

  // Give PropelAuth time to establish session after SSO redirect
  useEffect(() => {
    if (!isAuthLoading && isLoggedIn && accessToken) {
      const timer = setTimeout(() => {
        setSessionEstablished(true);
      }, 500); // Wait for PropelAuth to fully establish session

      return () => clearTimeout(timer);
    }
  }, [isAuthLoading, isLoggedIn, accessToken]);

  // Check login state when session is established
  useEffect(() => {
    if (sessionEstablished && isLoggedIn && accessToken && !hasCheckedLoginState) {
      checkLoginState();
    }
  }, [sessionEstablished, isLoggedIn, accessToken, hasCheckedLoginState]);

  const checkLoginState = async () => {
    if (hasCheckedLoginState) return;

    console.log('Checking login state after SSO...');
    setHasCheckedLoginState(true);

    try {
      const loginStateResponse = await fetchLoginState();

      await loginStateResponse.handle({
        success: ({ login_state }) => {
          console.log('Login state result:', login_state);
          switch (login_state) {
            case LoginState.USER_MISSING_REQUIRED_PROPERTIES:
              setShowUserPropertiesForm(true);
              break;
            case LoginState.LOGGED_IN:
              // Continue with normal user fetch flow
              break;
            default:
              console.log('Other login state:', login_state);
              break;
          }
        },
        unexpectedOrUnhandled: error => {
          console.error('Failed to check login state:', error);
        },
      });
    } catch (error) {
      console.error('Error checking login state:', error);
    }
  };

  const handleUserPropertiesSuccess = async () => {
    // Reset login state check and try again
    setHasCheckedLoginState(false);
    setShowUserPropertiesForm(false);
    await checkLoginState();
  };

  const handleUserPropertiesError = (error: string) => {
    setError(error);
  };

  useEffect(() => {
    // Only attempt to fetch user data once PropelAuth session is established and no user properties form is shown
    if (
      sessionEstablished &&
      isLoggedIn &&
      accessToken &&
      !userId &&
      !attemptedFetch &&
      !isUserLoading &&
      !showUserPropertiesForm
    ) {
      setAttemptedFetch(true);

      // Add a small delay before fetching to ensure auth state is stable
      const fetchTimer = setTimeout(() => {
        dispatch(getUser())
          .unwrap()
          .catch(error => {
            console.error('Failed to fetch user data:', error);
            setError('Failed to load user data. Please try again.');
          });
      }, 200);

      return () => clearTimeout(fetchTimer);
    }
  }, [
    sessionEstablished,
    isLoggedIn,
    accessToken,
    userId,
    dispatch,
    attemptedFetch,
    isUserLoading,
    showUserPropertiesForm,
  ]);

  // Set timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!userId && isLoggedIn && sessionEstablished && !showUserPropertiesForm) {
        console.error('Timeout occurred while fetching user data');
        setTimeoutOccurred(true);
        setError('Session timeout. Please try logging in again.');
      }
    }, 10000); // 10 seconds timeout

    return () => clearTimeout(timeout);
  }, [userId, isLoggedIn, sessionEstablished, showUserPropertiesForm]);

  // If not logged in (anymore), redirect to login
  if (!isAuthLoading && (!isLoggedIn || !accessToken)) {
    return <Navigate to="/auth/login" replace />;
  }

  // Show user properties form if needed
  if (showUserPropertiesForm) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: 3,
          maxWidth: 400,
          margin: '0 auto',
        }}
      >
        <UserPropertiesForm
          onSuccess={handleUserPropertiesSuccess}
          onError={handleUserPropertiesError}
        />
      </Box>
    );
  }

  // If we have user data, redirect to home
  if (userId && user) {
    return <Navigate to="/" replace />;
  }

  // If timeout occurred or there's an error, show error with retry option
  if (timeoutOccurred || error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Authentication Issue
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {error || 'There was a problem completing your sign-in. Please try again.'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
          <button
            onClick={() => performFullLogout(dispatch, logout)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#757575',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Sign Out
          </button>
        </Box>
      </Box>
    );
  }

  // Show loading state
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress color="inherit" size={30} />
      <Typography fontFamily="DM Mono" fontSize={16} color="text.primary">
        {!sessionEstablished
          ? 'Establishing session...'
          : showUserPropertiesForm
            ? 'Completing profile...'
            : isUserLoading
              ? 'Loading your profile...'
              : 'Completing sign-in...'}
      </Typography>
    </Box>
  );
}
